import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import TicketTableRow from "./TicketTableRow";
import TicketTableHeader from "./TicketTableHeader";
import TicketTablePagination from "./TicketTablePagination";

const STORAGE_KEY = "ticketTableSortConfig";

const TicketTable = ({ data = [] }) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Load sort config from localStorage
  useEffect(() => {
    const storedSort = localStorage.getItem(STORAGE_KEY);
    if (storedSort) {
      try {
        const parsed = JSON.parse(storedSort);
        if (parsed?.key && parsed?.direction) {
          setSortConfig(parsed);
        }
      } catch (e) {
        console.error("Invalid stored sort config:", e);
      }
    }
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    const keyMap = {
      ticket: "ticketid",
      status: "displayStatus",
      title: "workflowtype",
      created: "creationdate",
      pcn: "clientitemid",
      lob: "lob",
      checkNumber: "checkNumber",
      checkAmount: "checkAmount",
    };

    const actualKey = keyMap[sortConfig.key] || sortConfig.key;

    return [...data].sort((a, b) => {
      const aValue = a[actualKey];
      const bValue = b[actualKey];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const currentData = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get("page");

    if (page) {
      setCurrentPage(parseInt(page, 10));
    } else {
      const newUrl = `${location.pathname}?page=1`;
      window.history.pushState({}, "", newUrl);
    }
  }, [location.search]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const newDirection = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      const newSort = { key, direction: newDirection };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSort));
      return newSort;
    });
  };

  return (
    <>
      <div className="p-6 overflow-scroll px-0">
        <table className="mt-4 w-full min-w-max table-auto text-left resizable-table">
          <TicketTableHeader onSort={handleSort} sortConfig={sortConfig} />
          <tbody>
            {currentData.length ? (
              currentData.map((row) => (
                <TicketTableRow
                  key={row.id}
                  {...row}
                  checkNumber={row.metadata?.CheckNumber}
                  checkAmount={row.metadata?.Amount}
                  docClass={row.metadata?.DocClass}
                />
              ))
            ) : (
              <tr>
                <td className="p-2" colSpan={13}>
                  <p className="w-full mt-10 mb-10 py-10 bg-background-3 h-50 flex items-center justify-center rounded-md">
                    No tickets waiting your attention
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TicketTablePagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
        totalItems={data.length}
      />
    </>
  );
};

export default TicketTable;
